import { useEffect, useMemo, useState, useRef, useCallback } from "react"
import { useTranslation } from "react-i18next"
import {
  DndContext,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
  DragStartEvent,
  DragOverlay,
  rectIntersection,
} from "@dnd-kit/core"
import { arrayMove, sortableKeyboardCoordinates } from "@dnd-kit/sortable"
import { cloneDeep, isEqual } from "lodash"

import BasicButton from "@/components/button/BasicButton"
import {
  useReportTemplateDetailQuery,
  useReportTemplateMetasQuery,
  useUpsertReportTemplateMutation,
} from "@/hooks/api/useReportQuery"
import { REPORT_TYPE, SECTION_TYPE } from "@/app/report/common/constants"
import { isEqualUnordered } from "@/app/report/common/tools"
import { GROUP_KEY_MAPPING } from "@/app/report/hooks/useCustomTableData"
import {
  GraphSettings,
  PerformanceProfileSettings,
  ScatterChartSettings,
  SessionDetailsSettings,
  TacticMapSettings,
} from "@/app/report/common/type"
import { ReportTemplate, Section, StatsSettings, SummarySettings } from "@/types/report"
import { observeMuiErrors } from "@/utils/error"
import useRouter, { RouterEvent } from "@/hooks/router/useRoute"
import { useBlockBrowserBack } from "@/hooks/router/useBlockBrowserBack"
import { createGlobalStore } from "@/stores/global"

import SettingSectionContainer from "@/app/report/components/setting/SettingSectionContainer"
import SummarySection from "@/app/report/daily/settings/components/section/SummarySection"
import ScatterChartSection from "@/app/report/daily/settings/components/section/ScatterGraphSection"
import PlayerGraphSection from "@/app/report/daily/settings/components/section/PlayerGraphSection"
import SessionDetailsSection from "@/app/report/daily/settings/components/section/SessionDetailsSection"
import TacticMapSection from "@/app/report/daily/settings/components/section/TacticMapSection.tsx"
import { colors } from "@/components/colors"
import "./dailyReportSettings.scss"
import SettingTemplateSelector from "../../components/setting/SettingTemplateContainer"
import PerformanceProfileSection from "./components/section/PerformanceProfileSection"
import CustomTableSettingsSection from "./components/section/CustomTableSettingsSection"
import SectionDivider from "@/app/report/components/setting/SectionDivider"
import DeleteItemDialog from "@/app/report/components/setting/DeleteItemDialog"

export default function DailyReportSetting() {
  const { t } = useTranslation()
  const router = useRouter()
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  )

  const [idfTemplate, setIdfTemplate] = useState<number | null>(null)
  const [tempTemplateState, setTempTemplateState] = useState<ReportTemplate | null>(null)
  const [settingContentList, setSettingContentList] = useState<Section[]>([])
  const [draggingSectionKey, setDraggingSectionKey] = useState<string | null>(null)
  const [hasSectionError, setHasSectionError] = useState(false)
  const [isClickedSave, setIsClickedSave] = useState(false)
  const [hoveredPosition, setHoveredPosition] = useState<number | null>(null)
  const [isMenuOpen, setIsMenuOpen] = useState(false)
  const [deleteSectionIndex, setDeleteSectionIndex] = useState<number | null>(null)
  const containerRef = useRef<HTMLDivElement>(null)

  const setSnackBar = createGlobalStore((state) => state.setSnackBar)

  const { data: templates, refetch: templateMetaRefetch } = useReportTemplateMetasQuery({
    reportType: REPORT_TYPE.DAILY,
  })

  const { data: template, refetch: templateRefetch } = useReportTemplateDetailQuery(
    idfTemplate as number,
  )

  const { mutateAsync: reportTemplateMutate, isPending } = useUpsertReportTemplateMutation()

  useEffect(() => {
    if (idfTemplate !== null) return
    const lastUsed = templates?.find((template) => template.isLastUsed)

    if (lastUsed) {
      setIdfTemplate(lastUsed.idfTemplate)
    }
  }, [templates])

  useEffect(() => {
    if (template) {
      setTempTemplateState(cloneDeep(template) || null)
      setSettingContentList(cloneDeep(template.content) || [])
    }
  }, [template])

  useEffect(() => {
    let prevErr = false

    const observer = observeMuiErrors((hasErr) => {
      if (prevErr !== hasErr) {
        setHasSectionError(hasErr)
        prevErr = hasErr
      }
    })

    return () => {
      observer.disconnect()
    }
  }, [])

  const newSettingContentList = useMemo(
    () => [
      ...settingContentList,
      { section: "__dummy__placeholder__", active: false, settings: [] },
    ],
    [settingContentList],
  )

  const handleDragStart = (event: DragStartEvent) => {
    setDraggingSectionKey(event.active.id as string)
  }

  const handleDragEnd = (event: DragEndEvent) => {
    setDraggingSectionKey(null)

    const dragOver = document.querySelector(".SettingSectionDragOver")
    const dragOverOrigin = document.querySelector(".SettingSectionDragOverOrigin")
    if (
      dragOver?.nextElementSibling === dragOverOrigin ||
      dragOver?.previousElementSibling === dragOverOrigin
    ) {
      return
    }

    const { active, over } = event
    const oldIndex = newSettingContentList.findIndex(
      (cont, idx) => `${cont.section}-${idx}` === active?.id,
    )
    const newIndex = newSettingContentList.findIndex(
      (cont, idx) => `${cont.section}-${idx}` === over?.id,
    )

    const targetIndex = newIndex > oldIndex ? newIndex - 1 : newIndex === -1 ? oldIndex : newIndex

    setSettingContentList(arrayMove(settingContentList, oldIndex, targetIndex))
    setTempTemplateState((prev) => {
      if (!prev) {
        return null
      }

      return { ...prev, content: arrayMove(prev.content, oldIndex, targetIndex) }
    })
  }

  const draggingContent = useMemo(() => {
    if (!draggingSectionKey) return null
    const index = settingContentList.findIndex(
      (cont, idx) => `${cont.section}-${idx}` === draggingSectionKey,
    )
    return index >= 0 ? settingContentList[index] : null
  }, [draggingSectionKey, settingContentList])

  const availableSections = useMemo(() => {
    // Stats by Player 는 폐지 예정 — 신규 추가 불가, 기존 템플릿은 로드 시 Custom Table 로 대치된다 (#99)
    return [
      SECTION_TYPE.SUMMARY,
      SECTION_TYPE.SCATTER_GRAPH,
      SECTION_TYPE.GRAPH,
      SECTION_TYPE.SESSION_DETAILS,
      SECTION_TYPE.TACTIC_MAP,
      SECTION_TYPE.PERFORMANCE_PROFILE,
      SECTION_TYPE.CUSTOM_TABLE,
    ]
  }, [])

  const sectionCounts = useMemo(() => {
    const counts: Record<string, number> = {}
    settingContentList.forEach((section) => {
      counts[section.section] = (counts[section.section] || 0) + 1
    })

    return counts
  }, [settingContentList])

  const getDefaultSettings = (sectionType: string, currentCount: number = 0) => {
    switch (sectionType) {
      case SECTION_TYPE.SUMMARY:
        return [
          { title: "", metric: "", calcType: "average" },
          { title: "", metric: "", calcType: "average" },
          { title: "", metric: "", calcType: "average" },
        ]
      case SECTION_TYPE.SCATTER_GRAPH:
        return [
          {
            xAxis: "",
            yAxis: "",
            color: colors.blue[500],
            xAxisManualScaleActive: false,
            xAxisManualScaleValue: null,
            yAxisManualScaleActive: false,
            yAxisManualScaleValue: null,
          },
          {
            xAxis: "",
            yAxis: "",
            color: colors.primary[500],
            xAxisManualScaleActive: false,
            xAxisManualScaleValue: null,
            yAxisManualScaleActive: false,
            yAxisManualScaleValue: null,
          },
        ]
      case SECTION_TYPE.GRAPH:
        return [
          {
            title: `Chart by Player ${currentCount + 1}`,
            yAxis: [
              {
                metric: "",
                chartType: "",
                color: colors.blue[900],
                manualScaleActive: false,
                manualScaleValue: null,
              },
            ],
            sortSetting: { key: "jersey", order: "asc" },
            dataLabel: "hideValues",
            // 새 섹션의 기본 자릿수 — 설정 화면에서 바꿀 수 있고 화면·내보내기·빌더가 모두 쓴다
            decimalPlace: 1,
          },
        ]
      case SECTION_TYPE.SESSION_DETAILS:
        return [
          {
            calcType: "average",
            yAxis: [
              {
                metric: "",
                chartType: "",
                color: colors.platinum[500],
                manualScaleActive: false,
                manualScaleValue: null,
              },
            ],
            sortSetting: { key: "time", order: "asc" },
            dataLabel: "hideValues",
            decimalPlace: 1,
          },
        ]
      case SECTION_TYPE.TACTIC_MAP:
        return {
          title: `Map ${currentCount + 1}`,
          defaultMapType: "formationMap",
          defaultActionType: "R2",
          fixedColumns: ["number", "uniformNumber", "occurrence"],
          color: colors.red[700],
        }
      case SECTION_TYPE.PERFORMANCE_PROFILE:
        return {
          title: `Performance Profile ${currentCount + 1}`,
          yAxis: [
            {
              dataType: "",
              color: colors.blue[700],
              manualScaleActive: false,
              manualScaleValue: null,
            },
          ],
          actions: [],
        }
      case SECTION_TYPE.CUSTOM_TABLE: {
        const groupKey1 = "session"
        const { columnKey, info } = GROUP_KEY_MAPPING[groupKey1]

        return {
          title: `Table ${currentCount + 1}`,
          // 기본 그룹에서 유도된 컬럼은 기본 좌측 고정 (이후 해제 상태는 저장값을 존중 — WEB-119)
          fixedColumns: [columnKey, info].filter(Boolean),
          calcType: "average",
          metrics: [],
          groupKey1,
          // 새 Table 은 요약 행(Total·Average) 둘 다 표시가 기본 (#155)
          summaryRows: { total: true, average: true },
        }
      }
      default:
        return []
    }
  }

  useEffect(() => {
    if (!containerRef.current || draggingSectionKey || availableSections.length === 0) {
      return
    }

    const handleMouseMove = (e: MouseEvent) => {
      const container = containerRef.current
      if (!container) {
        return
      }

      const sections = container.querySelectorAll(".SettingSectionCont")
      const mouseY = e.clientY

      let closestPosition = 0
      let minDistance = Infinity

      const templateSelector = container.querySelector(".SettingTemplateSelectorContainer")
      if (templateSelector) {
        const rect = templateSelector.getBoundingClientRect()
        if (mouseY < rect.bottom + 20) {
          setHoveredPosition(null)

          return
        }
      }

      const saveButton = container.querySelector(".daily-settings-save-button")

      if (saveButton) {
        const rect = saveButton.getBoundingClientRect()
        if (mouseY > rect.top - 20) {
          setHoveredPosition(null)

          return
        }
      }

      let isOverSection = false
      sections.forEach((section, index) => {
        const rect = section.getBoundingClientRect()
        const sectionTop = rect.top
        const sectionBottom = rect.bottom

        if (mouseY >= sectionTop && mouseY <= sectionBottom) {
          isOverSection = true

          return
        }

        const distanceToTop = Math.abs(mouseY - sectionTop)
        if (distanceToTop < minDistance) {
          minDistance = distanceToTop
          closestPosition = index
        }

        const distanceToBottom = Math.abs(mouseY - sectionBottom)
        if (distanceToBottom < minDistance) {
          minDistance = distanceToBottom
          closestPosition = index + 1
        }
      })

      if (isOverSection) {
        setHoveredPosition(null)

        return
      }

      if (sections.length === 0) {
        closestPosition = 0
      }

      if (sections.length > 1) {
        const lastRealSection = sections[sections.length - 2]
        if (lastRealSection) {
          const rect = lastRealSection.getBoundingClientRect()
          const distanceFromBottom = mouseY - rect.bottom

          if (distanceFromBottom > 0 && distanceFromBottom <= 60) {
            closestPosition = sections.length - 1
          }
        }
      }

      setHoveredPosition(closestPosition)
    }

    const handleMouseLeave = () => {
      if (!isMenuOpen) {
        setHoveredPosition(null)
      }
    }

    const container = containerRef.current
    container.addEventListener("mousemove", handleMouseMove)
    container.addEventListener("mouseleave", handleMouseLeave)

    return () => {
      container.removeEventListener("mousemove", handleMouseMove)
      container.removeEventListener("mouseleave", handleMouseLeave)
    }
  }, [draggingSectionKey, availableSections.length, settingContentList.length, isMenuOpen])

  const handleMenuStateChange = useCallback((isOpen: boolean) => {
    setIsMenuOpen(isOpen)
  }, [])

  const handleAddSection = (sectionType: string, position: number) => {
    const currentCount = sectionCounts[sectionType] || 0
    const newSection: Section = {
      section: sectionType,
      active: true,
      settings: getDefaultSettings(sectionType, currentCount),
    }

    setSettingContentList((prev) => {
      const updated = [...prev]
      updated.splice(position, 0, newSection)

      return updated
    })

    setTempTemplateState((prev) => {
      if (!prev) {
        return null
      }

      const updatedContent = [...prev.content]
      updatedContent.splice(position, 0, newSection)

      return { ...prev, content: updatedContent }
    })

    setHoveredPosition(null)
    setIsMenuOpen(false)
  }

  const handleDeleteSection = (index: number) => {
    setSettingContentList((prev) => prev.filter((_, i) => i !== index))
    setTempTemplateState((prev) => {
      if (!prev) {
        return null
      }

      return { ...prev, content: prev.content.filter((_, i) => i !== index) }
    })
  }

  const renderSettingContent = (content: Section, index: number) => {
    switch (content.section) {
      case SECTION_TYPE.SUMMARY:
        return (
          <SummarySection
            key={`${content.section}-${index}`}
            settings={content.settings as SummarySettings[]}
            tempTemplate={tempTemplateState}
            setTempTemplate={setTempTemplateState}
            sectionIndex={index}
          />
        )
      case SECTION_TYPE.SCATTER_GRAPH:
        return (
          <ScatterChartSection
            key={`${content.section}-${index}`}
            settings={content.settings as ScatterChartSettings[]}
            tempTemplate={tempTemplateState}
            setTempTemplate={setTempTemplateState}
            sectionIndex={index}
          />
        )
      case SECTION_TYPE.GRAPH:
        return (
          <PlayerGraphSection
            key={`${content.section}-${index}`}
            settings={content.settings as GraphSettings[]}
            tempTemplate={tempTemplateState}
            setTempTemplate={setTempTemplateState}
            sectionIndex={index}
          />
        )
      case SECTION_TYPE.SESSION_DETAILS:
        return (
          <SessionDetailsSection
            key={`${content.section}-${index}`}
            settings={content.settings as SessionDetailsSettings[]}
            tempTemplate={tempTemplateState}
            setTempTemplate={setTempTemplateState}
            sectionIndex={index}
          />
        )
      case SECTION_TYPE.TACTIC_MAP:
        return (
          <TacticMapSection
            key={`${content.section}-${index}`}
            settings={content.settings as TacticMapSettings}
            tempTemplate={tempTemplateState}
            setTempTemplate={setTempTemplateState}
            sectionIndex={index}
          />
        )
      case SECTION_TYPE.PERFORMANCE_PROFILE:
        return (
          <PerformanceProfileSection
            key={`${content.section}-${index}`}
            settings={content.settings as PerformanceProfileSettings}
            tempTemplate={tempTemplateState}
            setTempTemplate={setTempTemplateState}
            sectionIndex={index}
          />
        )
      case SECTION_TYPE.CUSTOM_TABLE:
        return (
          <CustomTableSettingsSection
            key={`${content.section}-${index}`}
            settings={content.settings as StatsSettings}
            setTempTemplate={setTempTemplateState}
            sectionIndex={index}
          />
        )
      default:
        return null
    }
  }

  const isEqualContent = useMemo(() => {
    if (!tempTemplateState || !template) return true

    const isEqualTemplateName = template.name === tempTemplateState.name
    const isEqualContent = isEqual(tempTemplateState.content, template.content)

    return isEqualTemplateName && isEqualContent
  }, [tempTemplateState, template])

  const validateManualScales = useMemo(() => {
    if (!tempTemplateState) return { isValid: true, errors: [] }

    const errors: string[] = []

    tempTemplateState.content.forEach((section, sectionIndex) => {
      const sectionName = t(section.section)

      if (section.section === SECTION_TYPE.SCATTER_GRAPH) {
        const settings = section.settings as ScatterChartSettings[]
        settings.forEach((item, itemIndex) => {
          if (item.xAxisManualScaleActive) {
            if (item.xAxisManualScaleValue === null || item.xAxisManualScaleValue === undefined) {
              errors.push(
                `${sectionName} #${sectionIndex + 1}, Item ${itemIndex + 1}: X-Axis ${t("manualScale")} ${t("maxValue")} is required`,
              )
            } else if (item.xAxisManualScaleValue <= 0) {
              errors.push(
                `${sectionName} #${sectionIndex + 1}, Item ${itemIndex + 1}: X-Axis ${t("manualScale")} ${t("maxValue")} must be greater than 0`,
              )
            }
          }
          if (item.yAxisManualScaleActive) {
            if (item.yAxisManualScaleValue === null || item.yAxisManualScaleValue === undefined) {
              errors.push(
                `${sectionName} #${sectionIndex + 1}, Item ${itemIndex + 1}: Y-Axis ${t("manualScale")} ${t("maxValue")} is required`,
              )
            } else if (item.yAxisManualScaleValue <= 0) {
              errors.push(
                `${sectionName} #${sectionIndex + 1}, Item ${itemIndex + 1}: Y-Axis ${t("manualScale")} ${t("maxValue")} must be greater than 0`,
              )
            }
          }
        })
      }

      if (
        section.section === SECTION_TYPE.GRAPH ||
        section.section === SECTION_TYPE.SESSION_DETAILS
      ) {
        const settings = section.settings as GraphSettings[] | SessionDetailsSettings[]
        settings.forEach((item, itemIndex) => {
          item.yAxis.forEach((axis, axisIndex) => {
            if (axis.manualScaleActive) {
              if (axis.manualScaleValue === null || axis.manualScaleValue === undefined) {
                errors.push(
                  `${sectionName} #${sectionIndex + 1}, Chart ${itemIndex + 1}, Y-Axis ${axisIndex + 1}: ${t("manualScale")} ${t("maxValue")} is required`,
                )
              } else if (axis.manualScaleValue <= 0) {
                errors.push(
                  `${sectionName} #${sectionIndex + 1}, Chart ${itemIndex + 1}, Y-Axis ${axisIndex + 1}: ${t("manualScale")} ${t("maxValue")} must be greater than 0`,
                )
              }
            }
          })
        })
      }

      if (section.section === SECTION_TYPE.PERFORMANCE_PROFILE) {
        const settings = section.settings as PerformanceProfileSettings
        settings.yAxis.forEach((axis, axisIndex) => {
          if (axis.manualScaleActive) {
            if (axis.manualScaleValue === null || axis.manualScaleValue === undefined) {
              errors.push(
                `${sectionName} #${sectionIndex + 1}, Y-Axis ${axisIndex + 1}: ${t("manualScale")} ${t("maxValue")} is required`,
              )
            } else if (axis.manualScaleValue <= 0) {
              errors.push(
                `${sectionName} #${sectionIndex + 1}, Y-Axis ${axisIndex + 1}: ${t("manualScale")} ${t("maxValue")} must be greater than 0`,
              )
            }
          }
        })
      }
    })

    return { isValid: errors.length === 0, errors }
  }, [tempTemplateState, t])

  const isDisabledSaveButton = useMemo(() => {
    return isEqualContent || isPending || hasSectionError || !validateManualScales.isValid
  }, [isEqualContent, hasSectionError, isPending, validateManualScales.isValid])

  useBlockBrowserBack(!isEqualContent)

  useEffect(() => {
    if (isClickedSave) {
      return
    }
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (!isEqualContent) {
        e.preventDefault()
      }
    }
    const handleChangeState = () => {
      if (!isEqualContent) {
        const confirmed = confirm(t("messages.confirmLeavePage"))

        return !confirmed
      }
    }

    window.addEventListener("beforeunload", handleBeforeUnload)
    router?.addEventListener(RouterEvent.routeChangeStart, handleChangeState)

    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload)
      router?.removeEventListener(RouterEvent.routeChangeStart, handleChangeState)
    }
  }, [isEqualContent, router, isClickedSave])

  const onSave = async () => {
    if (!tempTemplateState) {
      return
    }

    if (!validateManualScales.isValid) {
      setSnackBar({
        open: true,
        message: validateManualScales.errors[0],
        severity: "error",
      })
      return
    }

    try {
      await reportTemplateMutate(tempTemplateState)
      await Promise.all([templateRefetch(), templateMetaRefetch()])
      setIsClickedSave(true)
    } catch (error) {
      console.error(error)
      await templateMetaRefetch()
      setIsClickedSave(false)
    }
  }

  useEffect(() => {
    if (isDisabledSaveButton && isClickedSave) {
      setSnackBar({
        open: true,
        message: t("messages.successUpdate"),
        severity: "success",
      })
    }
  }, [isDisabledSaveButton, isClickedSave])

  return (
    <div className="DailyReportSettingsContainer" ref={containerRef}>
      <SettingTemplateSelector
        idfCurrentTemplate={idfTemplate}
        template={tempTemplateState}
        setTemplate={setTempTemplateState}
        templates={templates || []}
        onChangeTemplate={setIdfTemplate}
        reportType="daily"
      />
      <DndContext
        sensors={sensors}
        collisionDetection={rectIntersection}
        onDragStart={handleDragStart}
        onDragEnd={handleDragEnd}>
        {newSettingContentList.map((content, index) => (
          <div key={`${content.section}-${index}`}>
            {!draggingSectionKey && availableSections.length > 0 && (
              <SectionDivider
                position={index}
                onAddSection={handleAddSection}
                availableSections={availableSections}
                isVisible={hoveredPosition === index}
                onMenuStateChange={handleMenuStateChange}
                sectionCounts={sectionCounts}
              />
            )}
            {content.section !== "__dummy__placeholder__" && (
              <SettingSectionContainer
                id={`${content.section}-${index}`}
                title={t(content.section)}
                onDeleteSection={() => setDeleteSectionIndex(index)}
                isLast={index === newSettingContentList.length - 1}>
                {renderSettingContent(content, index)}
              </SettingSectionContainer>
            )}
          </div>
        ))}
        <DragOverlay>
          {draggingContent ? (
            <SettingSectionContainer
              id={draggingContent.section}
              title={t(draggingContent.section)}
              isDragging
            />
          ) : null}
        </DragOverlay>
      </DndContext>

      <span className="daily-settings-save-button">
        <BasicButton
          type="primary-contained"
          size="lg"
          onClick={onSave}
          disabled={isDisabledSaveButton}>
          {t("button.save", "Save")}
        </BasicButton>
      </span>

      <DeleteItemDialog
        deleteItemIndex={deleteSectionIndex}
        setDeleteItemIndex={setDeleteSectionIndex}
        onDeleteAction={handleDeleteSection}
      />
    </div>
  )
}
